import type { CSSProperties, ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Card,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';
import { PermissionButton } from 'src/routes/components/permission-button';
import type { Vault, VaultKind } from 'src/pages/admin/services/vaultService';
import { paths } from 'src/routes/paths';

import { VAULTS_NS as NS, VAULTS_PERMISSION_RESOURCE as RESOURCE } from './constants';
import { canEditContact } from './contact';
import { capacityCells, formatCount, formatInstant } from './helpers';
import { VaultAddress } from './VaultAddress';
import { KindChip, StateChip } from './VaultChips';

/**
 * The list columns: PRD §14.4 (size cap and utilization in one column) plus the four §7.6 FR-13 capacity figures —
 * manager share value, TVL, size cap, remaining (Win, 2026-10-01: FR-13 wins over §14.4's detail-only placement).
 * The status chip sits beside the kind tag and the cut-off splits date / time, so every column fits a 1440 px screen.
 */
const COLUMNS = [
  'vault',
  'tvl',
  'sizeCapUtilization',
  'remaining',
  'managerPresentValue',
  'nav',
  'holders',
  'valuedAt',
] as const;
const NUMERIC = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const ID = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;

/**
 * XBIT-4247 (PRD §14.4 Ordering): a table row that can be dragged by its handle, with the keyboard too (focus the
 * handle, Space, the arrow keys, Space). The handle is offered only to a role that may reorder, and only where the
 * kind's order has been read; the rest of the row is not a drag target, so its links and buttons keep working.
 */
const SortableVaultRow = ({
  vault,
  enabled,
  handleLabel,
  children,
}: {
  vault: Vault;
  enabled: boolean;
  handleLabel: string;
  children: ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: vault.vaultId,
    disabled: !enabled,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 40, px: 1 }}>
        {enabled && (
          <PermissionButton resource={RESOURCE} action="reorder">
            <IconButton
              size="small"
              aria-label={handleLabel}
              sx={{ cursor: 'grab', touchAction: 'none' }}
              {...attributes}
              {...listeners}
            >
              <Iconify icon="solar:hamburger-menu-linear" />
            </IconButton>
          </PermissionButton>
        )}
      </TableCell>
      {children}
    </TableRow>
  );
};

export const VaultsTable = ({
  vaults,
  onEditContact,
  onReorder,
  canReorder,
}: {
  vaults: Vault[];
  /** XBIT-4401: the contact edit, offered on every vault but a CLOSED one. */
  onEditContact?: (vault: Vault) => void;
  /** XBIT-4247: a vault dropped on another's place (both ids are of one kind). Absent: no handles. */
  onReorder?: (fromId: string, toId: string) => void;
  /** XBIT-4247: whether a kind's order has been read, so its rows can be moved. Default: every kind. */
  canReorder?: (kind: VaultKind) => boolean;
}) => {
  const translate = useTranslate();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (onReorder && over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };
  const smDown = useResponsive('down', 'sm');
  const valuedAt = (v: Vault) => (v.valuation ? formatInstant(v.valuation.valuedAt) : '—');
  const editContact = (v: Vault) =>
    onEditContact && canEditContact(v.state) ? (
      <PermissionButton resource={RESOURCE} action="update">
        <Button
          size="small"
          variant="outlined"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => onEditContact(v)}
        >
          {translate(`${NS}.contact.edit`)}
        </Button>
      </PermissionButton>
    ) : null;

  // One desktop row's cells, shared by the plain and the sortable row.
  const rowCells = (v: Vault) => {
    const cells = capacityCells(v);
    return (
      <>
        <TableCell sx={{ minWidth: 170 }}>{identity(v, true)}</TableCell>
        <TableCell sx={NUMERIC}>{cells.tvl}</TableCell>
        <TableCell sx={NUMERIC}>
          {cells.sizeCap}
          {v.valuation && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: cells.overCap ? 'error.main' : 'text.secondary',
                fontWeight: cells.overCap ? 600 : 400,
              }}
            >
              {cells.utilization}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={NUMERIC}>{cells.remaining}</TableCell>
        <TableCell sx={NUMERIC}>{cells.managerPresentValue}</TableCell>
        <TableCell sx={NUMERIC}>{cells.nav}</TableCell>
        <TableCell sx={NUMERIC}>{formatCount(v.holderCount)}</TableCell>
        <TableCell sx={NUMERIC}>
          {valuedAt(v)
            .split(' ')
            .map((part) => (
              <div key={part}>{part}</div>
            ))}
        </TableCell>
        {onEditContact && <TableCell>{editContact(v)}</TableCell>}
      </>
    );
  };

  if (vaults.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.empty`)}
        </Typography>
      </Card>
    );
  }

  // The desktop row carries the status beside the kind tag; the phone card shows it top-right instead.
  const identity = (v: Vault, withState = false) => (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {/* XBIT-4239: the name opens the vault's detail page. */}
        <Link
          component={RouterLink}
          to={`${paths.dashboard.vaultVaults.root}/${encodeURIComponent(v.vaultId)}`}
          variant="subtitle2"
          underline="hover"
          sx={{ overflowWrap: 'break-word' }}
        >
          {v.name}
        </Link>
        <KindChip kind={v.kind} />
        {withState && <StateChip state={v.state} />}
      </Stack>
      <Typography variant="caption" sx={{ ...ID, color: 'text.secondary' }}>
        {v.vaultId}
      </Typography>
      <VaultAddress address={v.hlAddress} />
    </Box>
  );

  if (smDown) {
    return (
      <Stack spacing={2}>
        {vaults.map((v) => {
          const cells = capacityCells(v);
          return (
            <Card key={v.vaultId} sx={{ p: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                {identity(v)}
                <StateChip state={v.state} />
              </Stack>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  mt: 1.5,
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                }}
              >
                {[
                  ['tvl', cells.tvl],
                  ['sizeCap', cells.sizeCap],
                  ['remaining', cells.remaining],
                  ['utilization', cells.utilization],
                  ['managerPresentValue', cells.managerPresentValue],
                  ['nav', cells.nav],
                  ['holders', formatCount(v.holderCount)],
                  ['valuedAt', valuedAt(v)],
                ].map(([key, value]) => (
                  <Box key={key} sx={{ minWidth: 0 }}>
                    <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
                      {translate(`${NS}.table.${key}`)}
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, overflowWrap: 'anywhere' }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {editContact(v) && <Box sx={{ mt: 1.5 }}>{editContact(v)}</Box>}
            </Card>
          );
        })}
      </Stack>
    );
  }

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        {/* DndContext renders helper nodes of its own, which cannot sit inside a <tbody>. */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <Table size="small" sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                {onReorder && <TableCell sx={{ width: 40, px: 1 }} />}
                {COLUMNS.map((c) => (
                  <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                    {translate(`${NS}.table.${c}`)}
                  </TableCell>
                ))}
                {onEditContact && <TableCell>{translate(`${NS}.table.actions`)}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {onReorder ? (
                <>
                  {Array.from(new Set(vaults.map((v) => v.kind))).map((kind) => {
                    const ofKind = vaults.filter((v) => v.kind === kind);
                    return (
                      <SortableContext
                        key={kind}
                        items={ofKind.map((v) => v.vaultId)}
                        strategy={verticalListSortingStrategy}
                      >
                        {ofKind.map((v) => (
                          <SortableVaultRow
                            key={v.vaultId}
                            vault={v}
                            enabled={canReorder ? canReorder(v.kind) : true}
                            handleLabel={translate(`${NS}.order.handle`, { name: v.name })}
                          >
                            {rowCells(v)}
                          </SortableVaultRow>
                        ))}
                      </SortableContext>
                    );
                  })}
                </>
              ) : (
                vaults.map((v) => <TableRow key={v.vaultId}>{rowCells(v)}</TableRow>)
              )}
            </TableBody>
          </Table>
        </DndContext>
      </Box>
    </Card>
  );
};
